import { useLingui } from '@lingui/react/macro'
import * as Slider from '@radix-ui/react-slider'
import ColorHandle from 'assets/color-handle.svg?react'
import RangeHandleLeft from 'assets/range-handle.svg?react'
import { GeckoColorScale } from 'color-scale-kit'
import {
  DataFilterBackground,
  GeckoSummaryStatisticsHistogram,
} from 'features/dataLayers'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoColorScaleThreshold,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

const RangeHandleRight = styled(RangeHandleLeft)`
  transform: rotate(180deg);
`

const SliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
`

const SliderTrack = styled(Slider.Track)`
  position: relative;
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  height: 24px;
`

const SliderThumb = styled(Slider.Thumb)`
  display: block;
  width: max-content;
  background-color: transparent;
  z-index: 100;
  padding-top: 3px;

  &:focus {
    outline: none;
  }
`

const FilterSliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 50;
`

const ColorSliderContainer = styled.div`
  position: relative;
  height: 0;
  transform: translateY(-1.5px);
  z-index: 100;
`

const clamp = (value: GeckoMeasurement, range: GeckoMeasurementRange) => {
  return Math.max(Math.min(value.num, range.max.num), range.min.num)
}

export const RangeSlider: React.FC<{
  cdfId: string
  measurementUnit: GeckoMeasurementUnit
  fullValueRange: GeckoMeasurementRange
  filteredValueRange: GeckoMeasurementRange | undefined
  onFilteredValueRangeChange: (range: GeckoMeasurementRange) => void
  cdfStatsHistogram: GeckoSummaryStatisticsHistogram | undefined
  colorThreshold: GeckoColorScaleThreshold
  colorScale: GeckoColorScale
  onColorThresholdChanged: (threshold: GeckoColorScaleThreshold) => void
  minThresholdColor: string
  maxThresholdColor: string
  midThresholdColor: string
  compact?: boolean
}> = ({
  cdfId,
  measurementUnit,
  fullValueRange,
  filteredValueRange = fullValueRange,
  onFilteredValueRangeChange,
  cdfStatsHistogram,
  colorThreshold,
  colorScale,
  minThresholdColor,
  maxThresholdColor,
  midThresholdColor,
  compact = false,
  ...props
}) => {
  const { t } = useLingui()

  const { unit, step } = React.useMemo(() => {
    const unit = measurementUnit
    const scale = Math.ceil(Math.log10(fullValueRange.size.num))
    const step = 0.001 * Math.pow(10.0, scale)
    return { unit, fullValueRange, step }
  }, [measurementUnit, fullValueRange])

  const unitLabel =
    unit.name && unit !== GeckoMeasurementUnit.None ? ` (${unit.name})` : ''

  return (
    <>
      <div
        role='group'
        aria-label={t`Value range and color threshold controls${unitLabel}`}
        style={{
          height: compact ? '34px' : '40px ',
          paddingTop: compact ? '2px' : '4px',
        }}>
        <div style={{ position: 'relative' }}>
          <FilterSliderContainer>
            <SliderRoot
              aria-label={t`Value range filter`}
              min={fullValueRange.min.num}
              max={fullValueRange.max.num}
              value={[
                Math.max(filteredValueRange.min.num, fullValueRange.min.num),
                Math.min(filteredValueRange.max.num, fullValueRange.max.num),
              ]}
              step={step}
              onValueChange={([min, max]) => {
                onFilteredValueRangeChange(
                  GeckoMeasurementRange.CreateFromValues(min, max, unit)
                )
              }}>
              <SliderTrack />
              <SliderThumb
                aria-label={t`Minimum value`}
                style={{ cursor: 'e-resize' }}>
                <RangeHandleLeft style={{ cursor: 'e-resize' }} />
              </SliderThumb>
              <SliderThumb
                aria-label={t`Maximum value`}
                style={{ cursor: 'w-resize' }}>
                <RangeHandleRight style={{ cursor: 'w-resize' }} />
              </SliderThumb>
            </SliderRoot>
          </FilterSliderContainer>
          <DataFilterBackground
            cdfId={cdfId}
            valueRange={fullValueRange}
            histogram={cdfStatsHistogram}
            fullValueRange={fullValueRange}
            filteredValueRange={filteredValueRange}
            selectedColorScale={colorScale}
          />
        </div>
        <ColorSliderContainer>
          <SliderRoot
            aria-label={t`Color scale thresholds`}
            style={{ position: 'absolute', width: '100%' }}
            min={fullValueRange.min.num}
            max={fullValueRange.max.num}
            value={[
              // Don't show handles outside the data value range
              clamp(colorThreshold.min, fullValueRange),
              clamp(colorThreshold.mid, fullValueRange),
              clamp(colorThreshold.max, fullValueRange),
            ]}
            step={step}
            onValueChange={([min, mid, max]) =>
              props.onColorThresholdChanged(
                GeckoColorScaleThreshold.CreateFromValues(min, mid, max, unit)
              )
            }>
            <SliderTrack />
            <SliderThumb
              id='min'
              aria-label={t`Minimum color threshold`}
              style={{
                color: minThresholdColor,
                transform: 'translateY(-30%)',
              }}>
              <ColorHandle />
            </SliderThumb>
            <SliderThumb
              id='mid'
              aria-label={t`Middle color threshold`}
              style={{
                color: midThresholdColor,
                transform: 'translateY(-30%)',
              }}>
              <ColorHandle />
            </SliderThumb>
            <SliderThumb
              id='max'
              aria-label={t`Maximum color threshold`}
              style={{
                color: maxThresholdColor,
                transform: 'translateY(-30%)',
              }}>
              <ColorHandle />
            </SliderThumb>
          </SliderRoot>
        </ColorSliderContainer>
      </div>
    </>
  )
}
